import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Table } from "reactstrap";
import axios from "axios";
import moment from "moment";
import s from "./PeriodStrip.module.scss";

function PeriodStrip({ rows = [] }) {
  const getStatusClass = (status) => {
    const key = String(status || "").toLowerCase();
    if (key.includes("cleared but maintain")) return s.maintain;
    if (key === "activated" || key === "standby" || key === "cleared") return s.cleared;
    if (key === "accepted") return s.accepted;
    if (key === "no action") return s.noAction;
    if (key === "not-cleared" || key === "not cleared") return s.notCleared;
    return s.unknown;
  };

  // Group multiple entries occurring at the same period/time into one cell
  const grouped = [];
  const byTs = new Map();
  rows.forEach((r) => {
    const ts = r._eventTime ? r._eventTime.getTime() : (r.date ? new Date(r.date).getTime() : r.id);
    if (!byTs.has(ts)) {
      byTs.set(ts, {
        id: ts,
        date: r.date,
        period: r.period,
        isCurrent: !!r.isCurrent,
        entries: [{ type: r.type, status: r.displayStatus || r.status }],
      });
    } else {
      byTs.get(ts).entries.push({ type: r.type, status: r.displayStatus || r.status });
      if (r.isCurrent) byTs.get(ts).isCurrent = true;
    }
  });
  for (const v of byTs.values()) grouped.push(v);

  return (
    <div className={s.container}>
      <div className={s.grid}>
        {grouped.map((g) => {
          const title = `${g.date ? new Date(g.date).toLocaleDateString() : ""} | P${g.period} | ${g.entries.map(e => e.type+": "+e.status).join("; ")}`;
          const primaryStatus = g.entries[0]?.status;
          return (
            <div key={g.id} className={`${s.cell} ${getStatusClass(primaryStatus)} ${g.isCurrent ? s.current : ""}`} title={title}>
              <div className={s.period}>P{g.period}</div>
              <div className={s.badges}>
                {g.entries.map((e, idx) => (
                  <span key={idx} className={`${s.badge} ${getStatusClass(e.status)}`}>{e.type}: {e.status}</span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className={s.legend}>
        <div className={`${s.legendItem} ${s.cleared}`}>DR Activated / IL Standby</div>
        <div className={`${s.legendItem} ${s.maintain}`}>Cleared but maintain</div>
        <div className={`${s.legendItem} ${s.accepted}`}>Accepted</div>
        <div className={`${s.legendItem} ${s.noAction}`}>No action</div>
        <div className={`${s.legendItem} ${s.notCleared}`}>Not-cleared</div>
      </div>
    </div>
  );
}

export default function LastBidWidgetDemo({ facilityId}) {
  const [offers, setOffers] = useState([]);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem("lastBidViewMode") || "table";
    } catch (e) {
      return "table";
    }
  });
  const [downloadMonth, setDownloadMonth] = useState(() => moment().subtract(1, "month").format("YYYY-MM"));
  const [downloadStatus, setDownloadStatus] = useState("");

  const completedMonthOptions = Array.from({ length: 12 }, (_, index) => {
    const month = moment().subtract(index + 1, "month");
    return {
      value: month.format("YYYY-MM"),
      label: month.format("MMMM YYYY"),
    };
  });

  const getEventStartTime = (dateValue, periodValue) => {
    const period = typeof periodValue === "number" ? periodValue : parseInt(periodValue || 0, 10);
    const base = new Date(dateValue);
    if (Number.isNaN(base.getTime())) return null;
    const startOfDay = new Date(base.getFullYear(), base.getMonth(), base.getDate());
    const effectivePeriod = period > 0 ? period - 1 : 0;
    const minutesToAdd = effectivePeriod * 30;
    return new Date(startOfDay.getTime() + minutesToAdd * 60 * 1000);
  };

  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    if (/[",\n\r]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const downloadCsv = (filename, rows) => {
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getClearedDisplayStatus = (type, status, takenAmount) => {
    if (String(status).toLowerCase() !== "cleared") return status;
    if (parseFloat(takenAmount || 0) === 0) return "Cleared but maintain";
    return type === "DR" ? "Activated" : "Standby";
  };

  const handleDownloadMonth = async () => {
    if (!facilityId || !downloadMonth) return;

    setDownloadStatus("Preparing...");
    try {
      const limit = 10000;
      const [offersResp, bidsResp] = await Promise.all([
        axios.get(`/offers/cleared/${facilityId}`, {
          params: { limit, month: downloadMonth, clearedOnly: true },
        }),
        axios.get(`/bids/cleared/${facilityId}`, {
          params: { limit, month: downloadMonth },
        }),
      ]);

      const clearedOffers = (offersResp?.data || []).map((offer) => {
        const eventTime = getEventStartTime(offer.offer_date, offer.period);
        return {
          id: offer.id,
          type: "IL",
          date: offer.offer_date,
          period: offer.period,
          status: offer.offer_status,
          displayStatus: getClearedDisplayStatus("IL", offer.offer_status, offer.taken_amount),
          takenAmount: offer.taken_amount,
          eventTime,
        };
      });

      const clearedBids = (bidsResp?.data || []).map((bid) => {
        const eventTime = getEventStartTime(bid.bid_date, bid.period);
        return {
          id: bid.id,
          type: "DR",
          date: bid.bid_date,
          period: bid.period,
          status: bid.bid_status,
          displayStatus: getClearedDisplayStatus("DR", bid.bid_status, bid.taken_amount),
          takenAmount: bid.taken_amount,
          eventTime,
        };
      });

      const clearedEvents = [...clearedOffers, ...clearedBids]
        .filter((event) => event.eventTime)
        .sort((a, b) => b.eventTime.getTime() - a.eventTime.getTime());

      if (!clearedEvents.length) {
        setDownloadStatus("No cleared events");
        return;
      }

      const csvRows = [
        ["Date", "Time", "Type", "Period", "Status", "Source ID"].map(escapeCsvValue).join(","),
        ...clearedEvents.map((event) => [
          moment(event.eventTime).format("YYYY-MM-DD"),
          moment(event.eventTime).format("HH:mm:ss"),
          event.type,
          event.period,
          event.displayStatus,
          event.id,
        ].map(escapeCsvValue).join(",")),
      ];

      downloadCsv(`cleared-dr-il-${facilityId}-${downloadMonth}.csv`, csvRows);
      setDownloadStatus("Downloaded");
    } catch (error) {
      console.error("Error downloading cleared DR/IL events", error);
      setDownloadStatus("Failed");
    }
  };



  const isUnmountedRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const limit = 500;
      const [offersResp, bidsResp] = await Promise.all([
        axios.get(`/offers/cleared/${facilityId}?limit=${limit}`),
        axios.get(`/bids/cleared/${facilityId}?limit=${limit}`),
      ]);
      if (!isUnmountedRef.current) {
        setOffers(offersResp?.data || []);
        setBids(bidsResp?.data || []);
      }
    } catch (e) {
      if (!isUnmountedRef.current) setError(e?.message || "Failed to load offers/bids");
    } finally {
      if (!isUnmountedRef.current) setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    isUnmountedRef.current = false;
    fetchData();
    return () => { isUnmountedRef.current = true; };
  }, [fetchData]);

  // Poll at minute marks: 05, 25, 35, 55
  useEffect(() => {
    const targets = [5, 25, 35, 55]; // minutes
    let timerId;

    function scheduleNext() {
      const now = new Date();
      const currentMin = now.getMinutes();
      const currentSec = now.getSeconds();
      const currentMs = now.getMilliseconds();
      // find next target minute
      const nextTargetMin = targets.find((m) => m > currentMin) ?? targets[0];
      const nextDate = new Date(now);
      if (nextTargetMin <= currentMin) {
        // wrap to next hour
        nextDate.setHours(now.getHours() + 1, nextTargetMin, 0, 0);
      } else {
        nextDate.setMinutes(nextTargetMin, 0, 0);
      }
      const delay = nextDate.getTime() - now.getTime();
      timerId = window.setTimeout(async () => {
        await fetchData();
        scheduleNext();
      }, Math.max(0, delay - (currentSec * 1000 + currentMs)));
    }

    scheduleNext();
    return () => { if (timerId) window.clearTimeout(timerId); };
  }, [fetchData]);

  const rows = useMemo(() => {
    // Use Singapore time for period/window calculations
    const getSgtNow = () => {
      const formatter = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Singapore",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
      const parts = Object.fromEntries(
        formatter.formatToParts(new Date()).map((p) => [p.type, p.value])
      );
      const year = parseInt(parts.year, 10);
      const month = parseInt(parts.month, 10) - 1;
      const day = parseInt(parts.day, 10);
      const hour = parseInt(parts.hour, 10);
      const minute = parseInt(parts.minute, 10);
      const second = parseInt(parts.second, 10);
      return new Date(year, month, day, hour, minute, second);
    };

    const now = getSgtNow();

    const normalizedOffers = (offers || []).map((o) => ({
      id: `offer-${o.id}`,
      date: o.offer_date,
      period: o.period,
      status: o.offer_status,
      displayStatus: (String(o.offer_status).toLowerCase() === "cleared")
        ? ((parseFloat(o.taken_amount || 0) === 0) ? "Cleared but maintain" : "Standby")
        : o.offer_status,
      type: "IL",
      _eventTime: getEventStartTime(o.offer_date, o.period),
    }));
    const normalizedBids = (bids || []).map((b) => ({
      id: `bid-${b.id}`,
      date: b.bid_date || b.offer_date,
      period: b.period,
      status: b.bid_status || b.offer_status,
      displayStatus: (String(b.bid_status || b.offer_status).toLowerCase() === "cleared")
        ? ((parseFloat(b.taken_amount || 0) === 0) ? "Cleared but maintain" : "Activated")
        : (b.bid_status || b.offer_status),
      type: "DR",
      _eventTime: getEventStartTime(b.bid_date || b.offer_date, b.period),
    }));
    let combined = [...normalizedOffers, ...normalizedBids];

    const getCurrentPeriod1Based = () => {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const diffMinutes = Math.floor((now.getTime() - start.getTime()) / (60 * 1000));
      const p = Math.floor(diffMinutes / 30) + 1;
      if (p < 1) return 1;
      if (p > 48) return 48;
      return p;
    };
    const currentPeriod = getCurrentPeriod1Based();
    const existingEventTimes = new Set(
      combined
        .map((r) => (r._eventTime ? r._eventTime.getTime() : null))
        .filter((t) => t !== null)
    );

    // Determine baseline period from latest observed event at or before now; fallback to current period
    const pastEvents = combined
      .filter((r) => r._eventTime && r._eventTime <= now)
      .sort((a, b) => b._eventTime.getTime() - a._eventTime.getTime());
    const baseEvent = pastEvents.length ? pastEvents[0] : null;
    const baseDate = baseEvent ? new Date(baseEvent._eventTime.getTime()) : new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const basePeriod = baseEvent ? (parseInt(baseEvent.period || 0, 10) || 0) : currentPeriod;
    const baseStartTime = getEventStartTime(baseDate, basePeriod);
    const currentStartTime = getEventStartTime(now, currentPeriod);

    // Define fixed 12-period window: current-4 through current+7
    const fromTime = new Date(currentStartTime.getTime() - 4 * 30 * 60 * 1000);
    const toTime = new Date(currentStartTime.getTime() + 7 * 30 * 60 * 1000);

    // Helper to map datetime to 1-based period
    const getPeriodFromDateTime = (dt) => {
      const start = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
      const diffMinutes = Math.floor((dt.getTime() - start.getTime()) / (60 * 1000));
      return Math.floor(diffMinutes / 30) + 1;
    };

    // Ensure coverage: past 4 and future periods with no action show "No action"
    for (let ts = fromTime.getTime(); ts <= toTime.getTime(); ts += 30 * 60 * 1000) {
      if (!existingEventTimes.has(ts)) {
        const dt = new Date(ts);
        combined.push({
          id: `placeholder-${ts}`,
          date: dt,
          period: getPeriodFromDateTime(dt),
          status: "No action",
          displayStatus: "No action",
          type: "DR/IL",
          _eventTime: dt,
        });
      }
    }
    // Filter to the computed coverage window
    combined = combined.filter((r) => r._eventTime && r._eventTime >= fromTime && r._eventTime <= toTime);
    combined.sort((a, b) => {
      const aTime = a?._eventTime ? a._eventTime.getTime() : 0;
      const bTime = b?._eventTime ? b._eventTime.getTime() : 0;
      return aTime - bTime;
    });
    // Mark current period row for highlighting
    const currentTs = currentStartTime ? currentStartTime.getTime() : null;
    combined = combined.map((r) => ({
      ...r,
      isCurrent: r._eventTime && currentTs !== null && r._eventTime.getTime() === currentTs,
    }));
    return combined;
  }, [offers, bids]);

  // Build table rows to mirror the graph exactly: include placeholders and real actions.
  // Keep DR and IL as separate rows if both exist for the same period; placeholders use type "DR/IL".
  const tableRows = useMemo(() => {
    const list = Array.isArray(rows) ? [...rows] : [];
    list.sort((a, b) => {
      const ta = a._eventTime ? a._eventTime.getTime() : 0;
      const tb = b._eventTime ? b._eventTime.getTime() : 0;
      if (ta !== tb) return ta - tb;
      return String(a.type).localeCompare(String(b.type));
    });
    return list;
  }, [rows]);

  useEffect(() => {
    try { localStorage.setItem("lastBidViewMode", viewMode); } catch (e) {}
  }, [viewMode]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: "40vh", overflowY: "auto" }}>
      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end", alignItems: "center", width: "100%" }}>
        <select
          aria-label="Download cleared DR/IL month"
          value={downloadMonth}
          onChange={(event) => {
            setDownloadMonth(event.target.value);
            setDownloadStatus("");
          }}
          style={{
            height: 22,
            maxWidth: 120,
            borderRadius: 4,
            border: "1px solid #555",
            background: "transparent",
            color: "var(--body-color)",
            fontSize: 12,
          }}
        >
          {completedMonthOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          onClick={handleDownloadMonth}
          style={{
            padding: "2px 6px",
            borderRadius: 4,
            fontSize: 12,
            lineHeight: 1.2,
            border: "1px solid #188efc",
            background: "#188efc",
            color: "#fff",
          }}
        >
          Download
        </button>
        {downloadStatus && <span style={{ fontSize: 11 }}>{downloadStatus}</span>}
        <button
          onClick={() => setViewMode("table")}
          style={{
            padding: "2px 6px",
            borderRadius: 4,
            fontSize: 12,
            lineHeight: 1.2,
            border: viewMode === "table" ? "1px solid #8dd3c7" : "1px solid #555",
            background: "transparent",
            color: viewMode === "table" ? "#8dd3c7" : "var(--body-color)"
          }}
        >
          Table
        </button>
        <button
          onClick={() => setViewMode("strip")}
          style={{
            padding: "2px 6px",
            borderRadius: 4,
            fontSize: 12,
            lineHeight: 1.2,
            border: viewMode === "strip" ? "1px solid #8dd3c7" : "1px solid #555",
            background: "transparent",
            color: viewMode === "strip" ? "#8dd3c7" : "var(--body-color)"
          }}
        >
          Graph
        </button>
      </div>
      {loading && <div>Loading…</div>}
      {error && <div style={{ color: "#c00" }}>{error}</div>}

      {viewMode === "table" ? (
        <Table style={{ background: "#1e1e1e", color: "#fff", fontSize: "0.6rem" }}>
          <thead>
            <tr style={{ background: "#333" }}>
              <th style={{ padding: 4 }}>Date</th>
              <th style={{ padding: 4 }}>Type</th>
              <th style={{ padding: 4 }}>Period</th>
              <th style={{ padding: 4 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.length ? tableRows.map((r) => (
              <tr key={r.id} style={{ fontWeight: r.isCurrent ? 700 : 400 }}>
                <td style={{ padding: 4 }}>{r.date ? new Date(r.date).toLocaleDateString() : ""}</td>
                <td style={{ padding: 4 }}>{r.type}</td>
                <td style={{ padding: 4 }}>{r.period ?? ""}</td>
                <td style={{ padding: 4 }}>{r.displayStatus ?? r.status ?? ""}</td>
              </tr>
            )) : (
              <tr><td colSpan={5} style={{ padding: 4 }}>No cleared DR/IL results</td></tr>
            )}
          </tbody>
        </Table>
      ) : (
        <PeriodStrip rows={rows} />
      )}
    </div>
  );
}
